import {
  type CloudId,
  type CustomerId,
  type DeviceId,
  type ResellerId,
  type UserId,
  type VerifEyeGroupId,
} from '@solinkcorp/api-types';
import { type ExistingUserFeatureEnumKeysMapping } from '@solinkcorp/event-standard';
import { type AspenRolloutPolicy } from '@solinkcorp/user-features';
import { type CallHomeClientSettings, type CallHomeServiceMap } from '@solinkcorp/vd-common';

// Share the aspenWeb key with UserWithFeatures. A target type that has only optional properties requires one property in common.
declare module '@solinkcorp/vd-common' {
  interface CallHomeClientSettings {
    aspenWeb?: {
      rollout?: AspenRolloutPolicy;
    };
  }
}

export enum UserType {
  SOLINK = 'solink',
  CLOUD = 'cloud',
  RESELLER = 'reseller',
  ADMIN = 'admin',
  STANDARD = 'standard',
  CONNECT = 'connect',
}

// From https://github.com/SolinkCorp/call-home-server/blob/master/src/core/enums/user-identity-type.enum.ts
export enum UserIdentityType {
  AUTH0_USER = 'AUTH0_USER',
  GUEST_USER = 'GUEST_USER',
  API_KEY = 'API_KEY',
}

export type TimeFormat = 'twelvehour' | 'twentyfourhour';

export interface Verifeye4Preferences {
  sortBy?: 'newest' | 'oldest';
  eventTypes?: ('alarm' | 'access' | 'agent')[];
  limitResults?: 10 | 25 | 50;
  siteLabels?: string[];
  alarmZones?: string[];
}

/**
 * Per-user Data Explorer VerifEye reviewer-tab preferences (SL-15900). Mirrors the
 * alarm {@link Verifeye4Preferences} pattern. Persists the Open-tab sort and the
 * Closed-tab filter rail so both survive across sessions.
 *
 * There is no Date preference: Closed is a fixed 14-day retention window applied
 * server-side (reviewers can neither widen nor narrow it, so there is no Date
 * filter to remember), and reviewers cannot narrow their Open scope at all.
 */
export interface DataExplorerVerifeyePreferences {
  /** Open (and Closed) list sort. */
  sortBy?: 'newest' | 'oldest';
  /** Closed-tab employee id, '' = all employees. */
  closedEmployee?: string;
  /** Closed-tab VerifEye conclusion filter. */
  closedConclusion?: 'all' | 'yes' | 'no';
  /** Closed-tab VerifEye outcome value, '' = all outcomes. */
  closedOutcome?: string;
}

export interface UserPreferences {
  locale: string;
  dataExplorerBetaNoticeDismissed?: boolean | null;
  default360ViewMultiplay?: 'dewarped' | 'pinhole' | null;
  defaultHeatmapWindow?: number | null;
  defaultToHD?: boolean | null;
  defaultVideoFormat?: string | null;
  eventListLayoutType?: 'WATERFALL' | 'PEEK' | null;
  show360DewarpToggle?: boolean | null;
  showDateOnVideo?: boolean | null;
  showEventProgressInVideoPlayer?: boolean | null;
  TABLEVIEW_SETTINGS?: any | null;
  timeFormat?: TimeFormat | null;
  widgetRefreshEnabled?: boolean | null;
  aspenWebOptIn?: boolean | null;
  verifEyeDarkMode?: boolean | null;
  verifeye4?: Verifeye4Preferences | null;
  dataExplorerVerifeye?: DataExplorerVerifeyePreferences | null;
}

export interface CallHomeUser {
  id: UserId;
  email: string;
  phoneNumber?: string;
  eulaAcceptedAt: number | null;
  eulaAcceptedVersion: number | null;
  name?: string;
  userType: UserType;
  identityType: UserIdentityType;

  /**
   * Auth Provider information
   */
  primaryIdentityProvider: string;
  primaryIdentityConnection: string;

  // Ownership hierarchy
  cloudId?: CloudId;
  resellerId?: ResellerId;
  customerId?: CustomerId;
  deviceId?: DeviceId | null;
  widgetIds?: string[];
  creatorId?: string;

  cloudName?: string;
  resellerName?: string;
  customerName?: string;

  userRoleId?: string | null;
  cameraIds?: string[];
  locationIds?: string[];
  siteIds?: string[];
  locationGroupIds?: string[];
  isSiteAccessRestricted?: boolean;
  eventTypes?: number;

  metadata?: any;
  preferences?: UserPreferences;
  features?: {
    [feature: string]: boolean;
  };

  verifEyeGroupIds?: VerifEyeGroupId[];
  isSupportAccessEnabled?: boolean;
  supportAccessExpiresAt?: number | null;
  impersonatableUserIds?: string[];
  isDeploymentNotificationsEnabled?: boolean;
  isDigestEnabled?: boolean;
  isMfaEnabled?: boolean;

  lastLogin?: string | null; // ISO Date string
  createdAt?: string; // ISO Date string
  expiresAt?: string | null; // ISO Date string
  revokeTokensIssuedBefore?: string | null;

  // TODO: Fix these types.
  appMetadata?: any;
  userMetadata?: any;
  digestSettings?: any;
  reportSettings?: any;
}

export interface CallHomeAwsSettings {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  expiration: string;
}

export interface CallHomeCookie {
  name: string;
  value: string;
  domain: string;
}

export interface Eula {
  url: string;
  version: number;
}

export interface CallHomeAuthorizeResult {
  accessToken: string;
  clientSettings: CallHomeClientSettings;
  profile: CallHomeUser;
  aws?: CallHomeAwsSettings;
  cookies?: CallHomeCookie[];
  firebaseToken?: string;
  eula: Eula;
  widgetMetadata?: any;
  billingMetadata?: {
    stripeCustomerId?: string;
  };
  turnCredentials?: {
    username: string;
    credential: string;
  };
  serviceMap: CallHomeServiceMap;
  features: ExistingUserFeatureEnumKeysMapping[];
  biAccessToken?: string;
}

// Defined in user-credential.service.ts#getAccessTokenPayload:
// https://github.com/SolinkCorp/call-home-server/blob/develop/src/models/user/user-credential/user-credential.service.ts
export interface JWTUser {
  email: string;
  exp: number;
  features: {
    audio: boolean;
    downloads: boolean;
  };
  iat: number;
  iss: string;
  impersonated?: boolean;
  impersonator?: {
    user_id: string;
    email: string;
  };
  roles: string[];
  sub: string;
  tenantId: string;
  userType: UserType;
  app_metadata: {
    customerId?: string;
    identityType: 'AUTH0_USER' | 'GUEST_USER' | 'API_KEY';
    userType: UserType;
    // cloudId, resellerId, and tenantId are marked as opitional in call-home-service,
    // but I'm betting these are never null.  :P
    cloudId: string;
    resellerId: string;
    tenantId: string;
    deviceId: string | null;
    roles: string[];
    eventTypes: number | null;
    devices: string[] | null;
    groups: string[] | null;
    userRoleId: string[] | null;
    cloudOnly: boolean | null;
  };
  user_metadata: {
    support: boolean;
  };
}
